/**
 * IframeMode component
 */
import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';

import { Store } from '@ngrx/store';
import { get } from 'lodash';
import moment from 'moment';
import { Observable, Subscription } from 'rxjs';
import { filter, switchMap, tap } from 'rxjs/operators';

import { set_standalone_report_filters } from '@rpt-ngrx/actions/filters.action';
import { restore_token, set_iframe_mode } from '@rpt-ngrx/actions/session.action';
import { selectIsIframeMode, selectSessionState } from '@rpt-ngrx/selectors/session.selector';
import { TokenSessionService } from '@shared/services';
import { IUserSession } from '@shared/types/session';

@Component({
    selector: 'dm-iframe-mode',
    templateUrl: 'iframe-mode.component.html',
})
export class IframeModeComponent implements OnInit, OnDestroy {
    public error: boolean = false;
    private session$: Observable<IUserSession>;
    private subscription: Subscription[] = [];
    private navigateURL: string;
    private sourceTarget = null;

    constructor(
        private tokenSessionService: TokenSessionService,
        private router: Router,
        private route: ActivatedRoute,
        private store: Store,
    ) {}

    public ngOnInit() {
        this.session$ = this.store.select(selectSessionState);
        this.store.dispatch(set_iframe_mode({ isIframeMode: this.inIframe() }));
        this.subscription.push(
            this.route.params.subscribe((params) => {
                this.navigateURL = get(params, 'navigateURL', '/custom-reports');
                if (params['accessToken']) {
                    this.setToken(params['accessToken']);
                } else {
                    this.sourceTarget = this.route.snapshot.queryParams['target'];
                    if (this.sourceTarget) {
                        window.parent.postMessage(
                            {
                                msg_type: `GET_DATA_FROM_${this.sourceTarget}`,
                                platform: 'RPT',
                                timestamp: moment().toISOString(),
                                payload: null,
                            },
                            '*',
                        );
                    }
                    window.parent.postMessage(
                        {
                            msg_type: 'GET_ACCESS_TOKEN',
                            platform: 'RPT',
                            timestamp: moment().toISOString(),
                            payload: null,
                        },
                        '*',
                    );
                }
            }),
        );

        this.subscription.push(
            this.session$
                .pipe(
                    filter(({ user, token }) => !!user?.email && !!token),
                    filter(({ session_ready }) => !!session_ready),
                    switchMap(() =>
                        this.store.select(selectIsIframeMode).pipe(
                            tap((isIframe) => {
                                if (isIframe && this.route.snapshot.queryParams.report) {
                                    this.router.navigate(
                                        [this.navigateURL, 'report', `${this.route.snapshot.queryParams.report}`],
                                        { queryParams: { target: this.route.snapshot.queryParams.target } },
                                    );
                                } else {
                                    this.router.navigate([this.navigateURL]);
                                }
                            }),
                        ),
                    ),
                )
                .subscribe(),
        );
    }

    @HostListener('window:message', ['$event'])
    public iframeMessageListener(event: MessageEvent) {
        if (event && event.data && event.data['msg_type'] && event.data['msg_type'] === 'GET_ACCESS_TOKEN_SUCCESS') {
            this.setToken(event.data['payload']);
        }
        if (
            event &&
            event.data &&
            event.data['msg_type'] &&
            event.data['msg_type'] === `GET_DATA_FROM_${this.sourceTarget}_SUCCESS`
        ) {
            this.setToken(event.data['payload'].token);
            this.store.dispatch(set_standalone_report_filters({ standaloneReportFilters: event.data.payload.filters }));
        }
    }

    private setToken(accessToken: string) {
        try {
            const { token, user } = this.createToken(accessToken);
            this.tokenSessionService.saveToken(token);
            this.store.dispatch(restore_token({ token, user }));
        } catch {
            this.error = true;
        }
    }

    private inIframe() {
        return window.self !== window.top;
    }

    public ngOnDestroy() {
        this.subscription.map((s) => s.unsubscribe());
    }

    private createToken(token) {
        return {
            token,
            user: JSON.parse(atob(token.split('.')[1])),
        };
    }
}
